import dayjs from 'dayjs';
import { CircleX } from 'lucide-react';
import LZString from 'lz-string';
import { useEffect } from 'react';

import { check } from "../assets/svg/index.js";

import {
	isAlphanumeric, isValidEmail, isValidGSTNumber, isValidPhone, isValidString, lastNamevalidation
} from './DataValidationUtils.js';

import { fetchLocalStorageItem, setLocalStorageItem } from './StorageUtils.js';

import { flightReprice, flightSearch } from '../Services/APIs/Flight.js';

const FLIGHT_CLASS_CONSTANT = {
	ECONOMY: 'ECONOMY',
	PREMIUM_ECONOMY: 'PREMIUM_ECONOMY',
	FIRST: 'FIRST',
	BUSINESS: 'BUSINESS',
};

const FLIGHT_TRIP_CONSTANT = {
	ONEWAY: 'ONEWAY',
	ROUNDTRIP: 'ROUNDTRIP',
	MULTICITY: 'MULTICITY',
};

export const SuccessMessage = ({ message }) => {
	return (
		<>
			{message && (
				<div className="flex mt-2 gap-2 text-sm text-[#09CB11] font-Inter font-medium">
					<img src={check} alt="check" />
					{message}
				</div>
			)}
		</>
	)
}

export const ErrorMesage = ({ error, message }) => {
	return (
		<>
			{error && !message && (
				<div className="flex items-center mt-2 gap-2 text-sm text-red-600 font-Inter font-medium">
					<CircleX color="#e52424" size={18} />
					{error}
				</div>
			)}
		</>
	)
}

// Utility function to get subdomain information
export const getSubdomainInfo = () => {
	const hostname = window.location.hostname;
	const isLocalhost = hostname.includes("localhost");

	let subdomainName = null;
	let isSubdomain = false;

	// Check for subdomain in development environment
	if (isLocalhost) {
		const parts = hostname.split(".");
		// Assuming pattern subdomain.localhost
		if (parts.length > 1 && parts[0] !== "localhost") {
			subdomainName = parts[0];
			isSubdomain = true;
		}
	} else {
		// Check for subdomain in production environment
		const parts = hostname.split(".");
		// Check if there are more than two parts, indicating a subdomain is present
		if (parts.length > 2) {
			subdomainName = parts[0];
			isSubdomain = true;
		}
		// Check for specific subdomain and main domain
		if (subdomainName === "www") {
			isSubdomain = false; // It's the main domain
			subdomainName = null; // Clear subdomain name
		} else if (subdomainName && subdomainName !== "www") {
			isSubdomain = true; // Any other keyword is a valid subdomain
		}
	}

	return { isSubdomain, subdomainName };
};

// Component to handle main domain redirect
export const MainDomainRedirect = () => {
	const { isSubdomain } = getSubdomainInfo();
	const isMainDomain = !isSubdomain;

	useEffect(() => {
		if (isMainDomain) {
			window.location.href = 'https://www.adneto.in/product/bankco';
		}
	}, [isMainDomain]);

	return null;
};

export const isMobSDKMode = (search = window?.location?.search || "") => {
	const params = new URLSearchParams(search);
	const modParam = (params.get("mod") || "").trim().toLowerCase();
	return modParam === "mobsdk";
};

export const formatDiscountRoundOff = (discount) => {
	const rounded = Number(discount)?.toFixed(2);
	return rounded.endsWith('.00') ? parseInt(rounded) : parseFloat(rounded);
};

export const getFormattedSavingAmount = (offer = {}, fallback = "") => {
	const savingAmount = typeof offer === "string" ? offer : offer?.saving_amount;

	if (typeof savingAmount !== "string") {
		return fallback;
	}

	const normalizedSavingAmount = savingAmount.trim();

	if (!normalizedSavingAmount) {
		return fallback;
	}

	if (!normalizedSavingAmount.includes("##DISCOUNT##")) {
		return normalizedSavingAmount;
	}

	const rawDiscount = typeof offer === "object" && offer !== null ? offer?.discount : undefined;

	if (rawDiscount === undefined || rawDiscount === null || `${rawDiscount}`.trim() === "") {
		return normalizedSavingAmount;
	}

	const numericDiscount = Number(rawDiscount);

	if (Number.isNaN(numericDiscount)) {
		return normalizedSavingAmount;
	}

	return normalizedSavingAmount.replace(/##DISCOUNT##/g, `${formatDiscountRoundOff(numericDiscount)}`);
};

// Date Format Generate

// Array of month names
export const monthNames = ["January", "February", "March", "April", "May", "June",
	"July", "August", "September", "October", "November", "December"
];

// Function to add ordinal suffix to day
export function getOrdinalSuffix(day) {
	if (day > 3 && day < 21) return 'th';
	switch (day % 10) {
		case 1: return "st";
		case 2: return "nd";
		case 3: return "rd";
		default: return "th";
	}
}

export const colorDetect = (status) => {
	const normalizedStatus = String(status || "").toUpperCase();

	if (normalizedStatus === "REFUNDED") {
		return {
			text: "Refunded",
			color: "#6B7280",
		}
	} else if (normalizedStatus === "REFUND_INITIATED") {
		return {
			text: "Refund Initiated",
			color: "#6B7280",
		}
	} else if (normalizedStatus === "PENDING") {
		return {
			text: "Pending",
			color: "#ffbe41",
		}
	} else if (normalizedStatus === "PROCESSING") {
		return {
			text: "Pending",
			color: "#ffbe41",
		}
	} else if (normalizedStatus === "COMPLETED" || normalizedStatus === "SUCCESS") {
		return {
			text: "Completed",
			color: "#6dc965",
		}
	} else {
		return {
			text: "Not Available",
			color: "#D9D9D9",
		}
	}
}

// Lighten the color
export const hexToRgb = (hex) => {
	// Remove the hash (#) if present
	const cleanHex = hex?.replace("#", "");
	// Split into R, G, B components
	const bigint = parseInt(cleanHex, 16);
	return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

// Darken the color
// Utility function to darken a hex color
export const darkenColor = (hex, amount = 20) => {
	let color = hex.replace("#", "");
	if (color.length === 3) {
		color = color.split("").map((char) => char + char).join("");
	}
	const r = Math.max(0, Math.min(255, parseInt(color.substring(0, 2), 16) - amount));
	const g = Math.max(0, Math.min(255, parseInt(color.substring(2, 4), 16) - amount));
	const b = Math.max(0, Math.min(255, parseInt(color.substring(4, 6), 16) - amount));
	return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

// Utility function to reduce opacity of a hex color
export const lightenColor = (hex, opacityPercent = null) => {
	if (!hex) return '';
	if (opacityPercent === null) opacityPercent = 50;

	let color = hex.replace("#", "");
	if (color.length === 3) {
		color = color.split("").map((char) => char + char).join("");
	}
	// If invalid length, return original
	if (color.length !== 6) return hex;

	const r = parseInt(color.substring(0, 2), 16);
	const g = parseInt(color.substring(2, 4), 16);
	const b = parseInt(color.substring(4, 6), 16);

	if (opacityPercent > 0) {
		const alpha = Math.max(0, Math.min(1, 1 - opacityPercent / 100));
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	}

	return `#${color.toLowerCase()}`;
};

export const newTheme = {
	bank: {
		hex_code: "f5437f",
		text_hex_code: "ffffff",
		logo: null
	},
	brand: {
		hex_code: "f5437e",
		text_hex_code: "ffffff",
		logo: null
	},
	hex_code: "f5437e",
	text_hex_code: "ffffff",
	website_font_family: "Inter",
	website_font_url: "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap"
}
// change in the header component to change intogrator

// Common function for integrator theme color of text and background
export function backgroundColor(theme = newTheme) {
	return theme?.hex_code 
	? `#${theme?.hex_code}` 
	: theme?.bank?.hex_code 
		? `#${theme?.bank?.hex_code}` 
		: theme?.brand?.hex_code 
			? `#${theme?.brand?.hex_code}` 
			: "#EA2466";
};

export function textColor(theme = newTheme) {
	return theme?.text_hex_code 
	? `#${theme?.text_hex_code}` 
	: theme?.bank?.text_hex_code 
		? `#${theme?.bank?.text_hex_code}` 
		: theme?.brand?.text_hex_code 
			? `#${theme?.brand?.text_hex_code}` 
			: "#FFFFFF";
};

export function defaultTextColor(theme = newTheme) {
	return theme?.hex_code 
	? `#${theme?.hex_code}` 
	: theme?.bank?.hex_code 
		? `#${theme?.bank?.hex_code}` 
		: theme?.brand?.hex_code 
			? `#${theme?.brand?.hex_code}` 
			: "#EA2466";
};

const SubscriptionColor = {
	Offer: "#EA2466",
	Active: "#0AA910",
	Expiring: "#F58220",
	Expired: "#fa0e00"
}

// subscription related functions 
export function subscritptionDefaultColor(text) {
	return SubscriptionColor[text] || "#EA2466"
}

export const getSubscriptionBySlug = (subscriptionData, slug) => {
	// Return first item if array is empty or no slug provided
	if (!subscriptionData?.length || !slug) {
		return subscriptionData[0];
	}

	// Find subscription matching the slug
	const matchedSubscription = subscriptionData.find(
		subscription => subscription.slug === slug
	);

	// Return matched subscription or first item as fallback
	return matchedSubscription || subscriptionData[0];
};

// subscription data 
export const getStatusColor = (status) => {
	switch (status.toLowerCase()) {
		case 'paid':
			return 'border-[#58C3A8] text-[#58C3A8] px-5 py-2 border-2';
		case 'pending':
			return 'border-[#EFAF2D] text-[#EFAF2D] px-5 py-2 border-2';
		case 'failed':
			return 'border-[#E62929] text-[#E62929] px-5 py-2 border-2';
		default:
			return 'border-gray-600 text-gray-600 px-5 py-2 border-2';
	}
};

export const stopDetails = {
	1: { label: "Non Stop", color: "green" },
	2: { label: "1 Stop", color: "orange" },
	3: { label: "2+ Stops", color: "red" },
};

export const PUBLIC_LEVEL = {
	HOME: "HOME",
	REWARD_LISTING: "REWARD_LISTING",
	REWARD_DETAIL: "REWARD_DETAIL",
};

export const LANDING_PAGE = {
	HOME_PAGE: "HomePage",
	OFFER_PAGE: "OfferPage",
	REWARD_LISTING_PAGE: "RewardListingPage",
	REWARD_DETAIL_PAGE: "RewardDetailPage",
};

export function canAccessPublicRewardListing(publicLevel = null) {
	return publicLevel === PUBLIC_LEVEL.REWARD_LISTING || publicLevel === PUBLIC_LEVEL.REWARD_DETAIL;
}

export function canAccessPublicRewardDetail(publicLevel = null) {
	return publicLevel === PUBLIC_LEVEL.REWARD_DETAIL;
}

export function isRewardListingLandingPage(landingPage = null) {
	return landingPage === LANDING_PAGE.REWARD_LISTING_PAGE || landingPage === LANDING_PAGE.OFFER_PAGE;
}

export function isHomeLandingPage(landingPage = null) {
	return landingPage === LANDING_PAGE.HOME_PAGE || landingPage === "homepage";
}

export function filterSelectedFlightFromSegment(segment, selectedFlightKey) {
	const { outboundKey, inboundKey } = selectedFlightKey;
	const filteredFlights = {
		outboundFlight: null,
		inboundFlight: null,
		matchedOutboundBond: null,
		matchedInboundBond: null,
	};
	const parsedOutboundKey = outboundKey ? parseFlightKey(outboundKey) : null;
	const parsedInboundKey = inboundKey ? parseFlightKey(inboundKey) : null;

	if (segment?.Bonds?.length) {
		for (const bond of segment.Bonds) {
			const flight = { Bonds: [bond] };

			if (parsedOutboundKey && bond.BoundType === 'OutBound') {
				if (matchFlightToKey(flight, parsedOutboundKey)) {
					filteredFlights.outboundFlight = segment;
					filteredFlights.matchedOutboundBond = bond;
				}
			}

			if (parsedInboundKey && bond.BoundType === 'InBound') {
				if (matchFlightToKey(flight, parsedInboundKey)) {
					filteredFlights.inboundFlight = segment;
					filteredFlights.matchedInboundBond = bond;
				}
			}
		}
	} else {
		console.warn('No Bonds in segment:', segment);
	}
	return filteredFlights;
}

export function isPublicPage(publicLevel = null) {
	// Get the current URL path
	const currentPath = window.location.pathname;

	// Remove leading and trailing slashes for consistent comparison
	const normalizedPath = currentPath?.replace(/^\/|\/$/g, '');

	const isListingPublic = canAccessPublicRewardListing(publicLevel);
	const isDetailPublic = canAccessPublicRewardDetail(publicLevel);

	const isPublicRewardPreviewRoot =
		normalizedPath === "rewards" &&
		isListingPublic;
	const isPublicRewardPreviewDetail =
		isDetailPublic &&
		/^rewards\/(discount|cashback|giftcard|subscription)\/[^/]+\/[^/]+$/.test(normalizedPath);

	const isLegacyPublicRewardListing =
		isListingPublic &&
		/^rewards\/[^/]+\/[^/]+$/.test(normalizedPath);
	const isLegacyPublicRewardDetail =
		isDetailPublic &&
		/^rewards\/[^/]+\/[^/]+\/[^/]+\/[^/]+$/.test(normalizedPath);

	// Check if the path matches any public pattern
	return isPublicRewardPreviewRoot || isPublicRewardPreviewDetail || isLegacyPublicRewardListing || isLegacyPublicRewardDetail;
}

// * Generates a dynamic background image URL based on the screen size(mobile or desktop).
//  * Handles cases where the URL is null or invalid.
export const getDynamicBackgroundImageUrl = (url, isMobile) => {
	if (!url) {
		return ""; // Return empty string if the URL is null or undefined
	}

	try {
		// Only modify URL for mobile view
		if (isMobile) {
			// Case 1: Handle bg_image pattern URLs
			const bgImageParts = url.split("/bg_image/");
			if (bgImageParts.length === 2) {
				return `${bgImageParts[0]}/bg_image/mobile/${bgImageParts[1]}`;
			}

			// Case 2: Handle other URL patterns by inserting "mobile" before the last segment
			const urlObj = new URL(url);
			const pathParts = urlObj.pathname.split('/');

			// If there's at least one path segment
			if (pathParts.length > 1) {
				// Insert "mobile" before the last path segment
				pathParts.splice(pathParts.length - 1, 0, "mobile");

				// Reconstruct the URL
				urlObj.pathname = pathParts.join('/');
				return urlObj.toString();
			}
		}

		// Return the original URL if it's desktop view or if the URL structure is invalid
		return url;
	} catch (error) {
		console.error("Error generating background image URL:", error);
		return ""; // Return empty string in case of any error
	}
};

// Helper function to convert month abbreviation to month number
export const getMonthNumber = (monthAbbr) => {
	const months = {
		'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
		'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
	};
	return months[monthAbbr] || 1;
};

// Helper function to get departure date and time as a timestamp
export const getDepartureDateTime = (flight, fromCityCode) => {
	if (!flight || !flight.Bonds || !flight.Bonds[0] || !flight.Bonds[0].Legs) {
		return 0; // Return default value if structure isn't as expected
	}

	// Find the leg where the Origin matches the fromCityCode
	const matchingLeg = flight.Bonds[0].Legs.find(leg => leg?.Origin === fromCityCode);

	if (matchingLeg) {
		// Parse departure date and time into a timestamp
		const dateStr = matchingLeg.DepartureDate;
		const timeStr = matchingLeg.DepartureTime;

		// Handle date format like "Wed-26Feb2025"
		const [_, day, monthYear] = dateStr.match(/(\d+)([A-Za-z]+)(\d+)/) || [];
		const month = getMonthNumber(monthYear.slice(0, 3));
		const year = monthYear.slice(3);

		// Parse time in 24-hour format like "21:20"
		const [hours, minutes] = timeStr.split(':').map(Number);

		const timestamp = new Date(year, month - 1, day, hours, minutes).getTime();
		return timestamp;
	} else {
		// If no match is found, use the first leg's departure time as fallback
		const firstLeg = flight.Bonds[0].Legs[0];
		const dateStr = firstLeg.DepartureDate;
		const timeStr = firstLeg.DepartureTime;

		// Handle date format like "Wed-26Feb2025"
		const [_, day, monthYear] = dateStr.match(/([A-Za-z]+)-(\d+)([A-Za-z]+)(\d+)/) || [];
		const month = getMonthNumber(monthYear.slice(0, 3));
		const year = monthYear.slice(3);

		// Parse time in 24-hour format like "21:20"
		const [hours, minutes] = timeStr.split(':').map(Number);

		const timestamp = new Date(year, month - 1, day, hours, minutes).getTime();
		return timestamp;
	}
}

export function HighlightedText({ text, integratorTheme }) {
	// Match all text wrapped in #* and #* and split into parts
	const parts = text?.split(/(#\*.*?#\*)/)?.filter(Boolean);

	return (
		<div>
			{parts?.map((part, index) => {
				if (part?.startsWith("#*") && part?.endsWith("#*")) {
					// Remove the #* markers and wrap in span with highlight class
					const highlightedText = part?.slice(2, -2);

					return <span key={index} style={{ '--highlightedTextColor': defaultTextColor(integratorTheme) }} className="font-semibold highlightedText">{highlightedText}</span>;
				}

				// Render regular text
				return <span key={index}>{part}</span>;
			})}
		</div>
	);
};

// function to show loyalty points is available in module
export const showLoyaltyPoint = (theme) => {
	const baseModuleKeys = Object.keys(theme?.modules || {});
	const isLoyalityAvailable = baseModuleKeys?.includes("loyalty");
	return isLoyalityAvailable
};

export const parseDuration = (durationStr) => {
	const match = durationStr.match(/(\d+)h\s*(\d+)?m?/); // Extract hours & minutes
	if (!match) return 0;

	const hours = parseInt(match[1] || "0", 10);
	const minutes = parseInt(match[2] || "0", 10);

	return hours * 60 + minutes; // Convert total duration to minutes
};

// Convert minutes to "HH:mm" format
export const formatMinutesToHHMM = (minutes) => {
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	return `${String(hours).padStart(2, '0')}h ${String(mins).padStart(2, '0')}m`;
};

// Helper function to get DOB date range
export function getDOBDateRange(type, lastTravelDate) {
	const travelDate = dayjs(lastTravelDate || new Date());
	const today = dayjs();

	if (type === "Adult") {
		// Adult: Must be at least 12 years old
		const maxDate = travelDate.subtract(12, 'year');
		return { minDate: null, maxDate };
	} else if (type === "Child") {
		// Child: Between 2 and 12 years old
		const minDate = travelDate.subtract(12, 'year');
		const maxDate = travelDate.subtract(2, 'year');
		return { minDate, maxDate };
	} else if (type === "Infant") {
		// Infant: Between 15 days and 2 years old
		const minDate = travelDate.subtract(2, 'year');
		let maxDate = travelDate.subtract(15, 'day');
		if (maxDate.isBefore(today)) {
			maxDate = today; // Ensure maxDate is not in the past
		}
		return { minDate, maxDate };
	}

	return { minDate: null, maxDate: null };
}

// travel form validation

// Helper function to check if DOB is valid
export function isValidDOB(dob, type, lastTravelDate) {
	if (!dob) return false;

	const birthDate = dayjs(dob);
	if (!birthDate.isValid()) return false;

	const travelDate = dayjs(lastTravelDate);
	const today = dayjs();

	if (type === "Adult") {
		const maxDate = travelDate.subtract(12, 'year');
		return !birthDate.isAfter(maxDate, 'day');
	} else if (type === "Child") {
		const minDate = travelDate.subtract(12, 'year');
		const maxDate = travelDate.subtract(2, 'year');
		return (
			!birthDate.isBefore(minDate, 'day') &&
			!birthDate.isAfter(maxDate, 'day')
		);
	} else if (type === "Infant") {
		const minDate = travelDate.subtract(2, 'year');
		let maxDate = travelDate.subtract(15, 'day');
		if (maxDate.isBefore(today)) {
			maxDate = today; // Ensure maxDate is not in the past
		}
		return (
			!birthDate.isBefore(minDate, 'day') &&
			!birthDate.isAfter(maxDate, 'day')
		);
	}

	return false;
}

// Helper function to check passport expiry
export function isValidPassportExpiry(expiryDate, lastTravelDate) {
	if (!expiryDate) return false;

	const expiry = dayjs(expiryDate);
	if (!expiry.isValid()) return false;

	const sixMonthsAfterTravel = dayjs(lastTravelDate).add(6, 'month');
	return expiry.isAfter(sixMonthsAfterTravel);
}

// Traveller detail Validation functions
export const getTravellerDetailValidators = (type, lastTravelDate) => {
	const dobDateRange = getDOBDateRange(type, lastTravelDate);
	return {
		first_name: (value) => isValidString(value, true) ? "" : "First name is required and must contain only letters.",
		last_name: (value) => lastNamevalidation(value) ? "" : "Last name is required and must contain only letters and space.",
		email: (value) => value && !isValidEmail(value) ? "Invalid email address." : "",
		mobile_number: (value) => value && !isValidPhone(value) ? "Invalid phone number." : "",
		dob: (value) => {
			if (!value) return type === "Infant" ? "Date of birth is required." : "";
			const isValid = isValidDOB(value, type, lastTravelDate);
			if (!isValid) {
				if (type === "Adult") {
					return `Date of birth must be on or before ${dobDateRange.maxDate.format('DD/MM/YYYY')}.`;
				} else if (type === "Child") {
					return `Date of birth must be between ${dobDateRange.minDate.format('DD/MM/YYYY')} and ${dobDateRange.maxDate.format('DD/MM/YYYY')}.`;
				} else if (type === "Infant") {
					return `Date of birth must be between ${dobDateRange.minDate.format('DD/MM/YYYY')} and ${dobDateRange.maxDate.format('DD/MM/YYYY')}.`;
				}
			}
			return "";
		},
		passport_number: (value) => !value.trim() ? "Passport number is required." : "",
		// passport_expiry: (value) => !isValidPassportExpiry(value, lastTravelDate) ?
		// 	"Passport should be valid for at least 6 months from travel date." : "",
		passport_expiry: (value) =>
			!isValidPassportExpiry(value, lastTravelDate)
				? `Passport should be valid for at least 6 months from ${dayjs(lastTravelDate).format('DD/MM/YYYY')}.`
				: "",
		frequent_flyer_number: (value) => value && !isAlphanumeric(value) ?
			"Only alphanumeric characters are allowed." : ""
	}
};

// formst save traveller data
export const formatTravellerData = (formData, type) => {
	const { title, first_name, last_name, email, mobile_number, dob, passport_number, passport_expiry, frequent_flyer_airline, frequent_flyer_number, id } = formData;

	const traveller = {
		title,
		first_name,
		last_name,
		passenger_type: type?.charAt(0)?.toUpperCase() + type?.slice(1),
	};

	// Add optional fields if they are not empty
	if (id) traveller.id = id;
	if (email) traveller.email = email;
	if (mobile_number) traveller.mobile_number = mobile_number;
	if (dob) traveller.dob = dob;
	if (passport_number) traveller.passport_number = passport_number;
	if (passport_expiry) traveller.passport_expiry = passport_expiry;

	// Add frequent flyer details if both airline and number are provided
	if (frequent_flyer_airline && frequent_flyer_number) {
		traveller.frequent_flyer_details = {
			[frequent_flyer_airline]: frequent_flyer_number,
		};
	}

	return traveller;
};

// Enhanced validation functions
export const validateAllTravellers = (travellers, isInternational) => {
	const searchData = JSON.parse(fetchLocalStorageItem("flightSearchData") || "{}");

	// Ensure expectedCounts has default values if undefined
	const expectedCounts = {
		Adult: searchData?.travellers?.adults || 0,
		Child: searchData?.travellers?.children || 0,
		Infant: searchData?.travellers?.infants || 0
	};

	const actualCounts = travellers.reduce((acc, traveller) => {
		acc[traveller.passenger_type] = (acc[traveller.passenger_type] || 0) + 1;
		return acc;
	}, { Adult: 0, Child: 0, Infant: 0 });

	const countMismatchErrors = [];
	Object.keys(expectedCounts)?.forEach(type => {
		if (actualCounts[type] !== expectedCounts[type]) {
			countMismatchErrors.push({
				type,
				message: `Expected ${expectedCounts[type]} ${type}(s), but found ${actualCounts[type]}`
			});
		}
	});

	if (countMismatchErrors.length > 0) {
		setLocalStorageItem("travellerCountMismatch", JSON.stringify(countMismatchErrors));
		return true;
	} else {
		setLocalStorageItem("travellerCountMismatch", JSON.stringify([]));
	}

	const validationErrors = travellers.map((traveller, index) => ({
		passenger_type: traveller.passenger_type,
		original_index: traveller.originalIndex,
		errorFieldType: validateTraveller(traveller, isInternational)
	}));

	const hasFieldErrors = validationErrors.some(error => error.errorFieldType.length > 0);

	if (hasFieldErrors) {
		setLocalStorageItem("validationErrorState", JSON.stringify(validationErrors));
	} else {
		setLocalStorageItem("validationErrorState", JSON.stringify([]));
	}

	return hasFieldErrors;
};

export const validateTraveller = (traveller, isInternational) => {
	const errors = [];
	const requiredFields = {
		common: ['title', 'first_name', 'last_name'],
		infant: ['dob'],
		international: ['dob', 'passport_number', 'passport_expiry']
	};

	requiredFields.common?.forEach(field => {
		if (!traveller[field]) {
			errors.push({ type: field, message: `${field.replace('_', ' ')} is required` });
		}
	});

	if (traveller.passenger_type === 'Infant') {
		if (!traveller.dob) {
			errors.push({ type: 'dob', message: 'Date of birth is required for infants' });
		}
	}

	if (isInternational) {
		requiredFields.international?.forEach(field => {
			if (!traveller[field]) {
				errors.push({ type: field, message: `${field.replace('_', ' ')} is required for international travel` });
			}
		});
	}

	return errors;
};

// GST detail validaition function
export const getGSTDetailValidation = () => ({
	company_name: (value) =>
		value && !/^[a-zA-Z\s.]+$/.test(value)
			? "Company name must contain only letters, spaces, and dots."
			: !value
				? "Company name is required."
				: "",
	email: (value) => (!value ? "Email is required." : !isValidEmail(value) ? "Invalid email address." : ""),
	mobile_number: (value) => (!value ? "Mobile number is required." : !isValidPhone(value) ? "Invalid phone number." : ""),
	gstin: (value) => (!value ? "GST number is required." : !isValidGSTNumber(value) ? "Invalid GST number." : ""),
});

export const validateGSTDetail = (gstDetail) => {
	const errors = [];
	const requiredFields = ['gstin', 'company_name', 'email', 'mobile_number'];

	requiredFields.forEach(field => {
		if (!gstDetail[field]) {
			errors.push({ type: field, message: `${field.replace('_', ' ')} is required for GST` });
		}
	});

	return errors;
};

// Updated decode function to handle both URL params and login/signup data
export const encodeFlightSearchParams = (flightSearchData) => {
	try {
		const minimized = {
			t: {
				a: flightSearchData?.travellers?.adults || 0,
				c: flightSearchData?.travellers?.children || 0,
				i: flightSearchData?.travellers?.infants || 0
			},
			f: flightSearchData?.fromAirport || '',
			to: flightSearchData?.toAirport || '',
			cl: flightSearchData?.flightClass === FLIGHT_CLASS_CONSTANT.ECONOMY ? 'e' :
				flightSearchData?.flightClass === FLIGHT_CLASS_CONSTANT.PREMIUM_ECONOMY ? 'p' :
					flightSearchData?.flightClass === FLIGHT_CLASS_CONSTANT.BUSINESS ? 'b' : 'f',
			dd: flightSearchData?.departureDate || '',
			rd: flightSearchData?.returnDate || '',
			rt: flightSearchData?.isRoundTrip ? 1 : 0,
			cat: flightSearchData?.selectedTripCategory === FLIGHT_TRIP_CONSTANT.ROUNDTRIP ? 'r' :
				flightSearchData?.selectedTripCategory === FLIGHT_TRIP_CONSTANT.ONEWAY ? 'o' :
					flightSearchData?.selectedTripCategory === FLIGHT_TRIP_CONSTANT.MULTICITY ? 'm' : 'o'
		};
		return LZString.compressToEncodedURIComponent(JSON.stringify(minimized));
	} catch (error) {
		console.error('Error encoding flight search params:', error);
		return null;
	}
};

export const decodeFlightSearchParams = (compressedParams) => {
	try {
		let minimized;
		if (typeof compressedParams === 'string') {
			const decompressed = LZString.decompressFromEncodedURIComponent(compressedParams);
			if (!decompressed) return null;
			minimized = JSON.parse(decompressed);
		} else {
			minimized = compressedParams || {};
		}

		return {
			travellers: {
				adults: minimized?.t?.a || 0,
				children: minimized?.t?.c || 0,
				infants: minimized?.t?.i || 0
			},
			fromAirport: minimized?.f || {},
			toAirport: minimized?.to || {},
			flightClass: minimized?.cl === 'e' ? FLIGHT_CLASS_CONSTANT.ECONOMY :
				minimized?.cl === 'p' ? FLIGHT_CLASS_CONSTANT.PREMIUM_ECONOMY :
					minimized?.cl === 'b' ? FLIGHT_CLASS_CONSTANT.BUSINESS :
						FLIGHT_CLASS_CONSTANT.FIRST,
			departureDate: minimized?.dd || '',
			returnDate: minimized?.rd || '',
			isRoundTrip: minimized?.rt === 1,
			oneWayTrip: minimized?.rt === 0,
			selectedTripCategory: minimized?.cat === 'r' ? FLIGHT_TRIP_CONSTANT.ROUNDTRIP :
				minimized?.cat === 'o' ? FLIGHT_TRIP_CONSTANT.ONEWAY :
					FLIGHT_TRIP_CONSTANT.MULTICITY
		};
	} catch (error) {
		console.error('Error decoding flight search params:', error);
		return null;
	}
};

export const normalizeKeySegment = (str) => (str || '').replace(/\s+/g, '');

export const generateFlightKey = (flight) => {
	if (!flight?.Bonds?.[0]?.Legs?.length) return null;
	const { Bonds: [firstBond] } = flight;
	const { Legs: [firstLeg, ...rest] } = firstBond;
	const lastLeg = rest.length ? rest[rest.length - 1] : firstLeg;

	const carrier = normalizeKeySegment(firstLeg?.CarrierCode || firstLeg?.AirlineName || '');
	const flightNumber = normalizeKeySegment(firstLeg?.FlightNumber || '');
	const departureTime = firstLeg?.DepartureTime || '';
	const arrivalTime = lastLeg?.ArrivalTime || '';
	const formatDate = (date) => date ? dayjs(date).format('ddd-DDMMMYYYY') : '';

	return `${carrier}_${flightNumber}_${departureTime}_${formatDate(firstLeg?.DepartureDate || '')}_${arrivalTime}_${formatDate(lastLeg?.ArrivalDate || '')}`;
};

export const parseFlightKey = (key) => {
	if (!key) return null;
	const [carrier, flightNumber, departureTime, departureDate, arrivalTime, arrivalDate] = key.replace(/\s+/g, '')?.split('_');
	return {
		carrier: normalizeKeySegment(carrier),
		flightNumber: normalizeKeySegment(flightNumber),
		departureTime,
		departureDate,
		arrivalTime,
		arrivalDate
	};
};

// export const matchFlightToKey = (flight, parsedKey) => {
// 	if (!flight || !parsedKey) return false;
// 	return generateFlightKey(flight) === `${parsedKey?.carrier}_${parsedKey?.flightNumber}_${parsedKey?.departureTime}_${parsedKey?.departureDate}_${parsedKey?.arrivalTime}_${parsedKey?.arrivalDate}`;
// };

export const matchFlightToKey = (flight, parsedKey) => {
	if (!flight || !parsedKey) return false;

	const leg = flight?.Bonds?.[0]?.Legs?.[0];
	const lastLeg = flight?.Bonds?.[0]?.Legs?.slice(-1)[0];

	// Normalize time formats (e.g., '08:30' vs '8:30')
	const normalizeTime = (time) => time ? dayjs(time, 'HH:mm').format('HH:mm') : '';

	const flightCarrier = normalizeKeySegment(leg?.CarrierCode || leg?.AirlineName || '');
	const flightNumber = normalizeKeySegment(leg?.FlightNumber || '');
	const flightDepartureTime = normalizeTime(leg?.DepartureTime);
	const flightArrivalTime = normalizeTime(lastLeg?.ArrivalTime);
	const flightDepartureDate = leg?.DepartureDate ? dayjs(leg.DepartureDate).format('ddd-DDMMMYYYY') : '';
	const flightArrivalDate = lastLeg?.ArrivalDate ? dayjs(lastLeg.ArrivalDate).format('ddd-DDMMMYYYY') : '';

	const keyCarrier = normalizeKeySegment(parsedKey.carrier);
	const keyFlightNumber = normalizeKeySegment(parsedKey.flightNumber);
	const keyDepartureTime = normalizeTime(parsedKey.departureTime);
	const keyArrivalTime = normalizeTime(parsedKey.arrivalTime);

	const matches = (
		flightCarrier === keyCarrier &&
		flightNumber == keyFlightNumber &&
		flightDepartureTime === keyDepartureTime &&
		flightDepartureDate === parsedKey.departureDate &&
		flightArrivalTime === keyArrivalTime &&
		flightArrivalDate === parsedKey.arrivalDate
	);

	return matches;
};

// normalize flight data
export const normalizeFlightSearch = (flightSearch) => ({
	travellers: {
		adults: flightSearch?.travellers?.adults || 0,
		children: flightSearch?.travellers?.children || 0,
		infants: flightSearch?.travellers?.infants || 0
	},
	fromAirport: flightSearch?.fromAirport || '',
	toAirport: flightSearch?.toAirport || '',
	flightClass: flightSearch?.flightClass || 'ECONOMY',
	departureDate: flightSearch?.departureDate || '',
	returnDate: flightSearch?.returnDate || '',
	isRoundTrip: flightSearch?.isRoundTrip || false,
	oneWayTrip: flightSearch?.oneWayTrip !== undefined ? flightSearch.oneWayTrip : !(flightSearch?.isRoundTrip || false),
	selectedTripCategory: flightSearch?.selectedTripCategory || (flightSearch?.isRoundTrip ? 'ROUNDTRIP' : 'ONEWAY')
});

// Encoding flight data with flightSearch, filters, and selectedFlights
export const encodeFlightData = (data) => {
	try {
		const minimized = {
			fs: data.flightSearch ? {
				t: {
					a: data.flightSearch.travellers?.adults || 0,
					c: data.flightSearch.travellers?.children || 0,
					i: data.flightSearch.travellers?.infants || 0
				},
				f: data.flightSearch.fromAirport || '',
				to: data.flightSearch.toAirport || '',
				cl: data.flightSearch.flightClass === 'ECONOMY' ? 'e' :
					data.flightSearch.flightClass === 'PREMIUM_ECONOMY' ? 'p' :
						data.flightSearch.flightClass === 'BUSINESS' ? 'b' : 'f',
				dd: data.flightSearch.departureDate || '',
				rd: data.flightSearch.returnDate || '',
				rt: data.flightSearch.isRoundTrip ? 1 : 0
			} : null,
			f: data.filters ? {
				sS: { o: data.filters.selectedStops?.outbound || [], r: data.filters.selectedStops?.return || [] },
				sDT: { o: data.filters.selectedDepartureTimes?.outbound || [], r: data.filters.selectedDepartureTimes?.return || [] },
				sAT: { o: data.filters.selectedArrivalTimes?.outbound || [], r: data.filters.selectedArrivalTimes?.return || [] },
				pR: data.filters.priceRange || [0, 0],
				sA: data.filters.selectedAirlines || []
			} : null,
			sF: data.selectedFlights ? {
				o: data.selectedFlights.outboundKey || null,
				r: data.selectedFlights.inboundKey || null
			} : null
		};
		return LZString.compressToEncodedURIComponent(JSON.stringify(minimized));
	} catch (error) {
		console.error('Error encoding flight data:', error);
		return null;
	}
};

// Decoding flight data with flightSearch, filters, and selectedFlights
export const decodeFlightData = (compressedParams) => {
	try {
		if (typeof compressedParams !== 'string') return null;
		const decompressed = LZString.decompressFromEncodedURIComponent(compressedParams);
		if (!decompressed) return null;
		const minimized = JSON.parse(decompressed);
		return {
			flightSearch: minimized.fs ? {
				travellers: {
					adults: minimized.fs.t?.a || 0,
					children: minimized.fs.t?.c || 0,
					infants: minimized.fs.t?.i || 0
				},
				fromAirport: minimized.fs.f || '',
				toAirport: minimized.fs.to || '',
				flightClass: minimized.fs.cl === 'e' ? 'ECONOMY' :
					minimized.fs.cl === 'p' ? 'PREMIUM_ECONOMY' :
						minimized.fs.cl === 'b' ? 'BUSINESS' : 'FIRST',
				departureDate: minimized.fs.dd || '',
				returnDate: minimized.fs.rd || '',
				isRoundTrip: minimized.fs.rt === 1
			} : null,
			filters: minimized.f ? {
				selectedStops: {
					outbound: minimized.f.sS?.o || [],
					return: minimized.f.sS?.r || []
				},
				selectedDepartureTimes: {
					outbound: minimized.f.sDT?.o || [],
					return: minimized.f.sDT?.r || []
				},
				selectedArrivalTimes: {
					outbound: minimized.f.sAT?.o || [],
					return: minimized.f.sAT?.r || []
				},
				priceRange: minimized.f.pR || [0, 0],
				selectedAirlines: minimized.f.sA || []
			} : null,
			selectedFlights: minimized.sF ? {
				outboundKey: minimized.sF.o || null,
				inboundKey: minimized.sF.r || null
			} : null
		};
	} catch (error) {
		console.error('Error decoding flight data:', error);
		return null;
	}
};

export const encodeFlightDetailData = (data) => {
	try {		
		// Normalize data
		const normalizedData = {
			contact: data.contact || { email: '', phoneNo: '', countryCode: '+91' },
			travelers: Array.isArray(data.travelers) ? data.travelers : [],
			gst: data.gst || { isSelected: false, email: '', mobile_number: '', gstin: '', company_name: '', id: null },
			addons: {
				outbound: {
					seats: data.addons?.outbound?.seats || [],
					meals: data.addons?.outbound?.meals || [],
					baggage: data.addons?.outbound?.baggage || [],
				},
				inbound: {
					seats: data.addons?.inbound?.seats || [],
					meals: data.addons?.inbound?.meals || [],
					baggage: data.addons?.inbound?.baggage || [],
				},
			},
			payment: data.payment || { baseFare: 0, taxes: 0, otherFare: 0, totalFare: 0, pointsApplied: 0, netPayable: 0 },
			flightBookingWithSSRdata: data.flightBookingWithSSRdata || {},
			traceId: data.traceId || '',
		};

		const jsonString = JSON.stringify(normalizedData);
		const compressed = LZString.compressToEncodedURIComponent(jsonString);

		if (compressed.length > 2000) {
			console.warn('Compressed flightDetailData exceeds typical URL length limit');
		}
		return compressed;
	} catch (error) {
		console.error('encodeFlightDetailData error:', error);
		return '';
	}
};

export const decodeFlightDetailData = (compressedParams) => {
	try {
		if (typeof compressedParams !== 'string' || !compressedParams) {
			console.warn('Invalid or empty compressedParams');
			return {};
		}

		const decompressed = LZString.decompressFromEncodedURIComponent(compressedParams);
		if (!decompressed) {
			console.warn('Failed to decompress flightDetailData');
			return {};
		}

		const data = JSON.parse(decompressed);

		return {
			contact: data.contact || { email: '', phoneNo: '', countryCode: '+91' },
			travelers: Array.isArray(data.travelers) ? data.travelers : [],
			gst: data.gst || { isSelected: false, email: '', mobile_number: '', gstin: '', company_name: '', id: null },
			addons: {
				outbound: {
					seats: data.addons?.outbound?.seats || [],
					meals: data.addons?.outbound?.meals || [],
					baggage: data.addons?.outbound?.baggage || [],
				},
				inbound: {
					seats: data.addons?.inbound?.seats || [],
					meals: data.addons?.inbound?.meals || [],
					baggage: data.addons?.inbound?.baggage || [],
				},
			},
			payment: data.payment || { baseFare: 0, taxes: 0, otherFare: 0, totalFare: 0, pointsApplied: 0, netPayable: 0 },
			flightBookingWithSSRdata: data.flightBookingWithSSRdata || {},
			traceId: data.traceId || '',
		};
	} catch (error) {
		console.error('decodeFlightDetailData error:', error);
		return {};
	}
};


export const setBookingParamsInUrl = (navigate, page, data, step = 'summary') => {
	try {

		const urlParams = new URLSearchParams();

		// Add searchParam
		if (data.flightSearch) {
			const searchParam = encodeFlightSearchParams(data.flightSearch);
			if (searchParam) {
				urlParams.set('searchParam', searchParam);
			} else {
				console.warn('Failed to encode searchParam');
			}
		}

		// Add flightData
		if (data.flightSearch || data.filters || data.selectedFlights) {
			const flightData = encodeFlightData({
				flightSearch: data.flightSearch,
				filters: data.filters,
				selectedFlights: data.selectedFlights,
			});
			if (flightData) {
				urlParams.set('flightData', flightData);
			} else {
				console.warn('Failed to encode flightData');
			}
		}

		// Add flightDetailData for details page
		if (page === 'details') {
			const flightDetailData = encodeFlightDetailData({
				contact: data.contact || { email: '', phoneNo: '', countryCode: '+91' },
				travelers: data.travelers || [],
				gst: data.gst || {},
				addons: data.addons || { outbound: {}, inbound: {} },
				payment: data.payment || {},
				flightBookingWithSSRdata: data.flightBookingWithSSRdata || {},
				traceId: data.traceId || '',
			});
			if (flightDetailData) {
				urlParams.set('flightDetailData', flightDetailData);
			} else {
				console.error('Failed to encode flightDetailData');
			}
		}

		let url = '/travel/flight';
		if (page === 'search') {
			url += `?page=search&${urlParams.toString()}`;
		} else if (page === 'details') {
			url += `?page=details&step=${step}&${urlParams.toString()}`;
		}

		navigate(url);
	} catch (error) {
		console.error('setBookingParamsInUrl error:', error);
	}
};

export const validateContactInfo = () => {
	const contactInfo = JSON.parse(fetchLocalStorageItem("contactInfo") || "{}");
	const { email = '', phoneNo = '' } = contactInfo;

	if (!email || !isValidEmail(email)) {
		return "Please provide a valid email address.";
	}
	if (!phoneNo || !isValidPhone(phoneNo)) {
		return "Please provide a valid phone number.";
	}
	return '';
};

export const validJourneyDate = ({ date }) => {
	// Ensure the date is valid
	if (!date || !dayjs(date).isValid()) {
		return true; // Consider invalid dates as expired/invalid
	}

	// Get current date (without time for comparison)
	const currentDate = dayjs().startOf('day');
	// Convert input date to start of day for comparison
	const journeyDate = dayjs(date).startOf('day');

	// Return true if journey date is before current date, false otherwise
	return journeyDate.isBefore(currentDate);
};

export const findIndexByStep = (step) => {
	const validSteps = ['summary', 'travellers', 'addons', 'payment'];
	const stepsIndex = validSteps?.indexOf(step);
	return stepsIndex;
};


// Helper function to decode and normalize search parameters
const prepareSearchData = (searchParam) => {
	const flightSearchData = decodeFlightSearchParams(searchParam);
	const normalized = normalizeFlightSearch(flightSearchData);
	setLocalStorageItem('flightSearchData', JSON.stringify(normalized));
	return normalized;
};

// Helper function to construct search payload
const buildSearchPayload = ({ travellers, fromAirport, toAirport, flightClass, departureDate, returnDate, isRoundTrip, oneWayTrip, selectedTripCategory }) => {
	const departureDateFormatted = dayjs(departureDate).format('YYYY-MM-DD');
	const returnDateFormatted = returnDate ? dayjs(returnDate).format('YYYY-MM-DD') : null;
	return {
		route_type: selectedTripCategory,
		cabin_class: flightClass,
		routes: isRoundTrip
			? [
				{ org: fromAirport.iata_code, dest: toAirport.iata_code, dept_date: departureDateFormatted },
				{ org: toAirport.iata_code, dest: fromAirport.iata_code, dept_date: returnDateFormatted },
			]
			: oneWayTrip
				? [{ org: fromAirport.iata_code, dest: toAirport.iata_code, dept_date: departureDateFormatted }]
				: [],
		travellers,
	};
};

// Helper function to filter flights by BondType
const getFlightsByBondType = (flights, bondType) =>
	flights.filter(x => x?.BondType?.toLowerCase() === bondType);

// Helper function to filter repriced flights by BondType
const getRepricedByBondType = (flights, bondType) =>
	flights.filter(x => x?.Segments[0]?.BondType?.toLowerCase() === bondType);

// Helper function to check fare mismatch
const hasFareMismatch = (selected, repriced) =>
	selected.length && repriced.length &&
	selected[0]?.Fare?.TotalFareWithOutMarkUp !== repriced[0]?.Segments[0]?.Fare?.TotalFareWithOutMarkUp;

// Helper function to compute fare difference
const computeFareDifference = (selected, repriced) => {
	if (hasFareMismatch(selected, repriced)) {
		return {
			originalPrice: selected[0]?.Fare?.TotalFareWithOutMarkUp,
			updatedPrice: repriced[0]?.Segments[0]?.Fare?.TotalFareWithOutMarkUp,
		};
	}
	return {};
};

// Search Section: Initiates flight search
export const fetchSearchAPI = async (searchParam) => {
	try {
		const normalized = prepareSearchData(searchParam);
		const payload = buildSearchPayload(normalized);
		const result = await flightSearch(payload);
		setLocalStorageItem('airportsData', JSON.stringify(result?.data?.airports || {}));
		setLocalStorageItem('TraceId', result?.data?.trace_id);
		return result;
	} catch (error) {
		console.error('Search API failed:', error);
		return false;
	}
};

// Listing Section: Calls Search API, filters flights, reprices, and checks fare differences
export const flightListingAPI = async ({ searchParam, flightData }, setAlert, setIsLoading) => {
	try {
		setIsLoading(true);
		const searchResult = await fetchSearchAPI(searchParam);
		if (!searchResult) {
			setAlert({ show: true, message: 'Flight search failed', type: 'error' });
			setIsLoading(false);
			return false;
		}
		
		const flightSegment = searchResult?.data?.data;
		const hasValidFlights = flightSegment?.length > 0 && flightSegment.some((flight) => flight?.Segments?.length);
		if (!hasValidFlights) {
			setAlert({ show: true, message: 'No flights found', type: 'error' });
			setIsLoading(false);
			return false;
		}

		const flightDataDecoded = decodeFlightData(flightData);
		const selectedFlightsKeys = flightDataDecoded?.selectedFlights || {};
		const { outboundKey, inboundKey } = selectedFlightsKeys;
		const normalized = prepareSearchData(searchParam);
		const { isRoundTrip, travellers, flightClass, selectedTripCategory } = normalized;

		const matchedFlights = [];
		let fallbackFlight = null;
		flightSegment.filter((flight) => flight?.Segments?.length).forEach((flight, index) => {
			const isOutboundFlight = index === 0;
			flight.Segments.forEach((segment) => {
				const filtered = filterSelectedFlightFromSegment(segment, selectedFlightsKeys);
				if (filtered.outboundFlight && isOutboundFlight) matchedFlights.push(filtered.outboundFlight);
				if (filtered.inboundFlight && isRoundTrip) matchedFlights.push(filtered.inboundFlight);
				if (!fallbackFlight && segment?.Bonds?.length) fallbackFlight = segment;
			});
		});

		if (matchedFlights.length === 0 && fallbackFlight) {
			matchedFlights.push(fallbackFlight);
			setAlert({ show: true, message: 'Selected flights unavailable, using default flight', type: 'warning' });
		}

		if (matchedFlights.length === 0) {
			const errorMessage = outboundKey || inboundKey ? 'Selected flights not available' : 'No valid flights';
			setAlert({ show: true, message: errorMessage, type: 'error' });
			setIsLoading(false);
			return false;
		}

		const repricePayload = {
			route_type: selectedTripCategory,
			cabin_class: flightClass,
			routes: buildSearchPayload(normalized).routes,
			travellers,
			trace_id: searchResult?.data?.trace_id,
			segments: matchedFlights,
		};

		const repriceResult = await flightReprice(repricePayload);
		if (repriceResult?.status !== 200) {
			setAlert({ show: true, message: repriceResult?.data?.error || 'Failed to reprice flights', type: 'error' });
			setIsLoading(false);
			return false;
		}

		const repriceFlights = repriceResult?.data?.data?.filter((flight) => flight?.Segments !== null) || [];
		if (repriceFlights.length === 0) {
			setAlert({ show: true, message: 'No valid repriced flights', type: 'error' });
			setIsLoading(false);
			return false;
		}

		// Check fare differences (adapted from BookingExpiry)
		const selectedFlights = JSON.parse(localStorage.getItem('selectedFlights') || '[]') || matchedFlights;
		const [selectedOutBoundFlight, selectedInBoundFlight] = ['outbound', 'inbound'].map(type =>
			getFlightsByBondType(selectedFlights, type)
		);
		const [repricedOutBoundFlight, repricedInBoundFlight] = ['outbound', 'inbound'].map(type =>
			getRepricedByBondType(repriceFlights, type)
		);

		const outboundFareDifference = computeFareDifference(selectedOutBoundFlight, repricedOutBoundFlight);
		const inboundFareDifference = computeFareDifference(selectedInBoundFlight, repricedInBoundFlight);

		const updatedFlights = [
			repricedOutBoundFlight.length ? structuredClone(repricedOutBoundFlight[0].Segments[0]) : null,
			repricedInBoundFlight.length ? structuredClone(repricedInBoundFlight[0].Segments[0]) : null
		].filter(Boolean);

		if (!updatedFlights.length) {
			setAlert({ show: true, message: 'No valid flights after repricing', type: 'error' });
			setIsLoading(false);
			return false;
		}

		setLocalStorageItem('selectedFlights', JSON.stringify(updatedFlights));
		// setLocalStorageItem('fareDifferences', JSON.stringify({ outbound: outboundFareDifference, inbound: inboundFareDifference }));

		setIsLoading(false);
		return { selectedFlights: updatedFlights, fareDifferences: { outbound: outboundFareDifference, inbound: inboundFareDifference } };
	} catch (error) {
		setIsLoading(false);
		setAlert({ show: true, message: 'Error fetching flights', type: 'error' });
		console.error('Listing API failed:', error);
		return false;
	}
};

// Summary Section: Calls Listing, consolidates data, includes fare differences
export const flightSummaryAPI = async ({ searchParam, flightData }, setAlert, setIsLoading) => {
	try {
		const listingResult = await flightListingAPI({ searchParam, flightData }, setAlert, setIsLoading);
		return listingResult;
	} catch (error) {
		setAlert({ show: true, message: 'Error generating summary', type: 'error' });
		console.error('Summary API failed:', error);
		return false;
	}
};

// Traveller Section: Calls up to Summary, submits traveller details
export const travellerDetailsAPI = async ({ searchParam, flightData, travellerData }, setAlert, setIsLoading) => {
	try {
		const summaryResult = await flightSummaryAPI({ searchParam, flightData }, setAlert, setIsLoading);
		if (!summaryResult) {
			setAlert({ show: true, message: 'Failed to retrieve flight summary', type: 'error' });
			return false;
		}

	} catch (error) {
		setAlert({ show: true, message: 'Error saving traveller details', type: 'error' });
		console.error('Traveller API failed:', error);
		return false;
	}
};

// Add-ons Section: Calls up to Traveller, handles add-ons
export const addOnsAPI = async ({ searchParam, flightData, travellerData }, setAlert, setIsLoading) => {
	try {
		const travellerResult = await travellerDetailsAPI({ searchParam, flightData, travellerData }, setAlert, setIsLoading);
		if (!travellerResult) {
			setAlert({ show: true, message: 'Failed to process traveller details', type: 'error' });
			return false;
		}
	} catch (error) {
		setAlert({ show: true, message: 'Error processing add-ons', type: 'error' });
		console.error('Add-ons API failed:', error);
		return false;
	}
};

// Payment Summary Section: Calls up to Add-ons, finalizes booking
export const paymentSummaryAPI = async ({ searchParam, flightData, travellerData }, setAlert, setIsLoading) => {
	try {
		setIsLoading(true);
		const addOnsResult = await addOnsAPI({ searchParam, flightData, travellerData }, setAlert, setIsLoading);
		if (!addOnsResult) {
			setAlert({ show: true, message: 'Failed to process add-ons', type: 'error' });
			setIsLoading(false);
			return false;
		}
	} catch (error) {
		setIsLoading(false);
		setAlert({ show: true, message: 'Error processing payment', type: 'error' });
		console.error('Payment API failed:', error);
		return false;
	}
};

export const getRatingAvgReview = (rating) => {
	let review = '';
  if(rating >= 1 && rating <= 2.9) review = 'Average';
  else if(rating >= 3 && rating <= 3.4) review = 'Good';
  else if(rating >= 3.5 && rating <= 4.4) review = 'Very Good';
  else if(rating >= 4.5 && rating <= 5) review = 'Excellent';

	return review;
}

// Validation rules for traveller fields
export const validateField = (field, value) => {
	switch (field) {
		case 'title':
			return !value ? "Title is required" : "";
		case 'first_name':
			if (!value || value.trim() === "") return "First name is required";
			return isValidString(value, true, false) ? "" : "First name must contain only letters and spaces";
		case 'last_name':
			if (!value) return "Last name is required";
			return lastNamevalidation(value) ? "" : "Last name must contain only letters and spaces";
		case 'email':
			// Optional field, but if filled, must be valid
			if (value && value.trim() !== "") {
				return isValidEmail(value) ? "" : "Please enter a valid email address";
			}
			return "";
		case 'mobile_number':
			// Optional field, but if filled, must be valid
			if (value && value.trim() !== "") {
				return isValidPhone(value) ? "" : "Please enter a valid phone number";
			}
			return "";
		default:
			return "";
	}
};

// Helper function to validate subvariant ID
export const isValidSubvariant = (subId) => {
	if (subId === null || subId === undefined) return false;
	const numericSubId = Number(subId);
	if (isNaN(numericSubId) || numericSubId < 0) return false;
	return true;
};

export const getUrlSelectedCardId = (search = window?.location?.search || "") => {
	const params = new URLSearchParams(search);
	const urlCardId = params.get("card_id");
	return isValidSubvariant(urlCardId) ? Number(urlCardId) : null;
};

const isMatchingCardSelection = (card, targetId) => {
	if (targetId === null || targetId === undefined) return false;
	return Number(card?.id) === Number(targetId) || Number(card?.sub_variant?.id) === Number(targetId);
};

export const getPreferredCardSelection = (cards = [], fallbackCard = null, search = window?.location?.search || "") => {
	const urlCardId = getUrlSelectedCardId(search);
	const safeCards = Array.isArray(cards) ? cards : [];
	const matchedUrlCard = urlCardId !== null
		? safeCards.find((card) => isMatchingCardSelection(card, urlCardId))
		: null;
	const resolvedFallbackCard = fallbackCard || safeCards.find((card) => card?.is_default) || safeCards[0] || null;
	const selectedCard = urlCardId !== null
		? (matchedUrlCard || {})
		: (resolvedFallbackCard || {});
	const selectedCardId = matchedUrlCard
		? Number(matchedUrlCard?.sub_variant?.id)
		: urlCardId ?? (isValidSubvariant(resolvedFallbackCard?.sub_variant?.id) ? Number(resolvedFallbackCard?.sub_variant?.id) : null);

	return {
		selectedCard,
		selectedCardId,
		isUrlCardSelection: urlCardId !== null && selectedCardId === urlCardId,
	};
};

// credit card information
// render bank display name
export function renderBankDisplayName(card) {
	return card?.sub_variant?.bank?.display_name !== "*" ? `${card?.sub_variant?.bank?.display_name } `: "";
};

// render subvarient name
export function renderSubvarientName(card) {
	return card?.sub_variant?.name !== "*" ? `${card?.sub_variant?.name } `: "";
};

// render payment network name
export function renderPaymentNetworkName(card) {
	return `${card?.sub_variant?.payment_network?.name !== "*" ? `${card?.sub_variant?.payment_network?.name} ` : ""}`;
};

// render varient name
export function renderVarientName(card) {
	return `${card?.sub_variant?.variant?.name !== "*" ? `${card?.sub_variant?.variant?.name} ` : ""}`;
};

// render Card Information
export function renderCardInformation(card, showBankName = true) {
	return(
		<>
			{showBankName && renderBankDisplayName(card)}
			{renderSubvarientName(card)}
			{renderPaymentNetworkName(card)}
			{renderVarientName(card)}
		</>
	);
};
